import { useState } from 'react';
import { useGuild } from '../contexts/GuildContext';
import { useToast } from '../contexts/ToastContext';
import { useCreatePunishment } from '../hooks/usePunishments';
import { getErrorMessage } from '../api/errorHandler';
import DashboardLayout from '../components/DashboardLayout';
import ServerSelector from '../components/ServerSelector';
import MemberSelector from '../components/MemberSelector';
import PunishmentTypeSelector from '../components/PunishmentTypeSelector';
import DurationInput from '../components/DurationInput';

export default function PunishmentPage() {
  const { selectedGuildId, setSelectedGuildId } = useGuild();
  const { showToast } = useToast();
  const createPunishment = useCreatePunishment(selectedGuildId);

  const [selectedMember, setSelectedMember] = useState<string>('');
  const [punishmentType, setPunishmentType] = useState<'warn' | 'timeout' | 'kick' | 'ban'>('warn');
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState<number | undefined>(undefined);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedMember) {
      showToast('error', '멤버를 선택해주세요');
      return;
    }

    if (!reason.trim()) {
      showToast('error', '제재 사유를 입력해주세요');
      return;
    }

    if ((punishmentType === 'timeout' || punishmentType === 'ban') && !duration) {
      showToast('error', '제재 기간을 입력해주세요');
      return;
    }

    try {
      await createPunishment.mutateAsync({
        userId: selectedMember,
        type: punishmentType,
        reason,
        duration,
      });

      showToast('success', '제재가 성공적으로 실행되었습니다');
      
      // 폼 초기화
      setSelectedMember('');
      setPunishmentType('warn');
      setReason('');
      setDuration(undefined);
    } catch (error) {
      showToast('error', getErrorMessage(error));
    }
  };

  return (
    <DashboardLayout>
      <div className="p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">제재 관리</h1>
              <p className="text-gray-600">멤버에게 수동으로 제재를 실행하세요</p>
            </div>
            <div className="mt-4 md:mt-0 md:w-80">
              <ServerSelector
                selectedGuildId={selectedGuildId}
                onSelectGuild={setSelectedGuildId}
              />
            </div>
          </div>

          {!selectedGuildId ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-700">서버를 선택해주세요</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    멤버 선택
                  </label>
                  <MemberSelector
                    guildId={selectedGuildId}
                    selectedMemberId={selectedMember}
                    onSelectMember={setSelectedMember}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    제재 유형
                  </label>
                  <PunishmentTypeSelector
                    selectedType={punishmentType}
                    onSelectType={setPunishmentType}
                  />
                </div>

                {(punishmentType === 'timeout' || punishmentType === 'ban') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      제재 기간 (초)
                    </label>
                    <DurationInput
                      value={duration}
                      onChange={setDuration}
                      type={punishmentType}
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    제재 사유
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="제재 사유를 입력하세요"
                  />
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={createPunishment.isPending}
                    className="px-6 py-2 bg-gradient-to-r from-[#FF7A7A] to-[#DD8DFF] text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                  >
                    {createPunishment.isPending ? '실행 중...' : '제재 실행'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
